import { Countdown } from '@/components/ui/Countdown';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { Separator } from '@ophelos/ui/components/separator';

interface PlanSummaryHeroProps {
  instalment: number;
  currency: string;
  paymentCount: number;
  total: number;
  /** Pre-discount figures; shown struck through beside the live ones when present. */
  originalInstalment?: number;
  originalTotal?: number;
  expiresAt?: string | null;
}

export function PlanSummaryHero({
  instalment,
  currency,
  paymentCount,
  total,
  originalInstalment,
  originalTotal,
  expiresAt,
}: PlanSummaryHeroProps) {
  const { t } = useTranslation();
  return (
    <div className="text-center w-full">
      <div className="stack gap-1.5 items-center pt-5 bg-soft border rounded-xl">
        <p className="select-none text-xs font-semibold uppercase tracking-wider text-accent-foreground/75">{t('paymentPlan.youllPay')}</p>
        {originalInstalment !== undefined && (
          <div className="flex flex-col items-center gap-1">
            <CurrencyDisplay amount={originalInstalment} currency={currency} className="text-sm line-through text-destructive" />
            <Countdown to={expiresAt} />
          </div>
        )}
        <div className="text-4xl font-bold text-accent-foreground">
          <CurrencyDisplay amount={instalment} currency={currency} className="text-4xl" centsClassName="text-2xl" />
        </div>
        <Separator className="bg-border my-2" />
        <dl className="stack gap-2 text-sm text-left w-full px-4 pb-4">
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-sm font-semibold text-muted-foreground">{t('paymentPlan.plan')}:</dt>
            <dd className="flex items-baseline gap-1 font-medium text-foreground tabular-nums text-base">
              {paymentCount} × <CurrencyDisplay amount={instalment} currency={currency} className="text-base" />
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-sm font-semibold text-muted-foreground">{t('paymentPlan.total')}</dt>
            <dd className="flex items-baseline gap-2 font-medium">
              {originalTotal !== undefined && (
                <CurrencyDisplay amount={originalTotal} currency={currency} className="text-sm line-through text-destructive" />
              )}
              <CurrencyDisplay amount={total} currency={currency} className="text-base" />
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
